import { createClient } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn("Supabase URL or ANON KEY is missing! Check your .env file.");
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// helper: sum existing reserved people for a given paiva + kattaus
async function getReservedCount(paiva, kattausId) {
  const { data, error } = await supabase
    .from("varaukset")
    .select("ihmiset")
    .eq("paiva", paiva)
    .eq("kattaus", kattausId);

  if (error) {
    console.error("getReservedCount error:", error);
    throw error;
  }

  return (data || []).reduce((sum, row) => sum + (row.ihmiset || 0), 0);
}

export async function fetchPaivat() {
  const { data, error } = await supabase.from("paivat").select("*");
  if (error) {
    console.error("fetchPaivat error:", error);
    throw error;
  }
  return data;
}

// Fetch sittings (kattaukset).
// Optional params:
// - paiva: when provided, calculates availability by subtracting existing reservations for that paiva
// - minPeople: filter out sittings that don't have at least minPeople available
export async function fetchKattaukset({ paiva = null, minPeople = 1 } = {}) {
  const { data: kattaukset, error } = await supabase.from("kattaukset").select("*");
  if (error) {
    console.error("fetchKattaukset error:", error);
    throw error;
  }

  // If no paiva provided, just filter by raw kapasiteetti
  if (!paiva) {
    return (kattaukset || []).filter(k => (k.kapasiteetti ?? 0) >= (minPeople || 0))
      .map(k => ({ ...k, available: k.kapasiteetti ?? 0 }));
  }

  // When paiva is provided, compute reserved sums per sitting and derive availability.
  const results = [];
  for (const k of kattaukset || []) {
    const reserved = await getReservedCount(paiva, k.id);
    const kapasiteetti = k.kapasiteetti ?? 0;
    const available = kapasiteetti - reserved;
    if (available >= (minPeople || 0)) {
      results.push({ ...k, available });
    }
  }
  return results;
}

export async function fetchKapasiteetit() {
  const { data, error } = await supabase.from("kapasiteetit").select("*");
  if (error) {
    console.error("fetchKapasiteetit error:", error);
    throw error;
  }
  return data;
}

// Fetch the kapasiteetti (capacity) for a single kattaus by id.
// Returns the kapasiteetti number or null if not found.
export async function fetchKapasiteettiForKattaus(kattausId) {
  if (kattausId === undefined || kattausId === null) {
    throw new Error("kattausId is required");
  }

  const { data, error } = await supabase
    .from("kattaukset")
    .select("kapasiteetti")
    .eq("id", kattausId)
    .single();

  if (error) {
    console.error("fetchKapasiteettiForKattaus error:", error);
    throw error;
  }

  return (data && typeof data.kapasiteetti === "number") ? data.kapasiteetti : null;
}

// New: insert a reservation into public.varaukset
// This will check capacity for the given paiva + kattaus and reject if exceeding kapasiteetti.
export async function createVaraus({ paiva, kattaus, ihmiset, varaaja, puhelin, sposti, info } = {}) {
   const payload = {
     paiva: paiva ?? null,
     kattaus: kattaus ?? null,
     ihmiset: ihmiset ?? null,
     // sanitize varaaja: ensure string, trim, limit length; otherwise null
     varaaja: typeof varaaja === "string" ? varaaja.trim().slice(0, 200) : (varaaja ? String(varaaja).trim().slice(0, 200) : null),
  puhelin: puhelin ?? null,
  sposti: sposti ?? null,
  // sanitize info: optional text up to 1000 chars
  info: typeof info === "string" ? info.trim().slice(0, 1000) : null,
  };

  // log payload that will be inserted (helps debug accidental paste of code into name)
  console.debug("createVaraus: final payload to insert:", {
    paiva: payload.paiva,
    kattaus: payload.kattaus,
    ihmiset: payload.ihmiset,
    varaaja_length: payload.varaaja ? payload.varaaja.length : 0,
    puhelin: payload.puhelin,
    sposti: payload.sposti,
    info_length: payload.info ? payload.info.length : 0,
  });

  // validate inputs
  const requested = Number(payload.ihmiset || 0);
  if (!payload.paiva || !payload.kattaus) {
    throw new Error("Valitse päivä ja kattaus.");
  }
  if (requested <= 0) {
    throw new Error("Vierasmäärän tulee olla suurempi kuin nolla.");
  }

  // fetch kattaus kapasiteetti
  const { data: kattausRow, error: kattausError } = await supabase
    .from("kattaukset")
    .select("id, kapasiteetti")
    .eq("id", payload.kattaus)
    .single();

  if (kattausError) {
    console.error("createVaraus fetch kattaus error:", kattausError);
    throw kattausError;
  }
  if (!kattausRow) {
    throw new Error("Valitsemaasi kattausta ei ole olemassa ???.");
  }

  const kapasiteetti = kattausRow.kapasiteetti ?? 0;

  // Check if the kattaus has enough kapasiteetti for the requested people
  if (kapasiteetti < requested) {
    const msg = `Varausta ei voitu vahvistaa, sillä joku ehti ennen sinua. Valitsemassasi kattauksessa on enää ${kapasiteetti} paikkaa vapaana.`;
    console.warn("createVaraus capacity:", msg);
    const err = new Error(msg);
    // mark as user-facing so Book.js can detect and show it
    err.isUserFacing = true;
    throw err;
  }

  // Subtract requested people from kattaukset.kapasiteetti
  // Use a conditional update to avoid racing decrements when kapasiteetti is insufficient
  const { data: updatedKattaus, error: updateError } = await supabase
    .from("kattaukset")
    .update({ kapasiteetti: kapasiteetti - requested })
    .eq("id", payload.kattaus)
    .gte("kapasiteetti", requested)
    .select()
    .single();

  if (updateError) {
    console.error("createVaraus update kattaus error:", updateError);
    throw updateError;
  }
  if (!updatedKattaus) {
    throw new Error("Failed to update capacity; insufficient capacity or concurrent change.");
  }

  // Insert the reservation
  const { data, error } = await supabase
    .from("varaukset")
    .insert(payload)
    .select()
    .single();

  if (error) {
    console.error("createVaraus error:", error);
    throw error;
  }
  return data;
}

// Admin: every reservation, newest first. Sorted server-side so the list does
// not depend on the order Postgres happens to return rows in.
export async function fetchVaraukset() {
  const { data, error } = await supabase
    .from("varaukset")
    .select("*")
    .order("id", { ascending: false });
  if (error) {
    console.error("fetchVaraukset error:", error);
    throw error;
  }
  return data || [];
}

// Admin: sittings with their raw stored kapasiteetti, i.e. the seats still
// free. fetchKattaukset() above is built for the booking flow and filters
// rows out; the admin table has to show all of them.
export async function fetchKattauksetAll() {
  const { data, error } = await supabase
    .from("kattaukset")
    .select("*")
    .order("paiva", { ascending: true })
    .order("aika", { ascending: true });
  if (error) {
    console.error("fetchKattauksetAll error:", error);
    throw error;
  }
  return data || [];
}

// Admin gate: hand the attempt to Postgres and get back yes/no. The stored
// hash lives in public.admin_secrets, which the anon key cannot select; the
// only thing exposed is this function. See
// supabase/migrations/20260916000000_admin_password.sql.
export async function verifyAdminPassword(password) {
  const { data, error } = await supabase.rpc("verify_admin_password", {
    pw: String(password ?? ""),
  });
  if (error) {
    console.error("verifyAdminPassword error:", error);
    throw new Error("Salasanan tarkistus epäonnistui. Onko verify_admin_password ajettu kantaan?");
  }
  return data === true;
}
